import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { format } from "date-fns";
import {
  ArrowLeft,
  Save,
  Power,
  PowerOff,
  ExternalLink,
  BadgeCheck,
  ShieldOff,
} from "lucide-react";
import {
  adminApi,
  type AdminCharityDetail,
  type UpdateCharityPatch,
} from "@/lib/api";
import { queryKeys } from "@/lib/queries";
import { useDirtyGuardedSeed } from "@/hooks/useDirtyGuardedSeed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/charities_/$id")({
  component: CharityDetailPage,
});

interface FormState {
  name: string;
  description: string;
  taxId: string;
  email: string;
  website: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  logoUrl: string;
  bannerUrl: string;
}

function fromDetail(d: AdminCharityDetail): FormState {
  return {
    name: d.name,
    description: d.description,
    taxId: d.taxId ?? "",
    email: d.email ?? "",
    website: d.website ?? "",
    phone: d.phone ?? "",
    street: d.address?.street ?? "",
    city: d.address?.city ?? "",
    state: d.address?.state ?? "",
    zipCode: d.address?.zipCode ?? "",
    logoUrl: d.logoUrl ?? "",
    bannerUrl: d.bannerUrl ?? "",
  };
}

function diffToPatch(initial: FormState, current: FormState): UpdateCharityPatch {
  const patch: UpdateCharityPatch = {};
  if (current.name !== initial.name) patch.name = current.name;
  if (current.description !== initial.description) patch.description = current.description;
  if (current.taxId !== initial.taxId) {
    patch.taxId = current.taxId.trim() === "" ? null : current.taxId.trim();
  }
  if (current.email !== initial.email) patch.email = current.email;
  if (current.website !== initial.website) {
    patch.website = current.website.trim() === "" ? null : current.website.trim();
  }
  if (current.phone !== initial.phone) {
    patch.phone = current.phone.trim() === "" ? null : current.phone.trim();
  }
  const addressChanged =
    current.street !== initial.street ||
    current.city !== initial.city ||
    current.state !== initial.state ||
    current.zipCode !== initial.zipCode;
  if (addressChanged) {
    patch.address = {
      street: current.street || undefined,
      city: current.city || undefined,
      state: current.state || undefined,
      zipCode: current.zipCode || undefined,
    };
  }
  if (current.logoUrl !== initial.logoUrl) {
    patch.logoUrl = current.logoUrl.trim() === "" ? null : current.logoUrl.trim();
  }
  if (current.bannerUrl !== initial.bannerUrl) {
    patch.bannerUrl = current.bannerUrl.trim() === "" ? null : current.bannerUrl.trim();
  }
  return patch;
}

function CharityDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [initial, setInitial] = useState<FormState | null>(null);
  const [actionMessage, setActionMessage] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);

  const detail = useQuery({
    queryKey: queryKeys.charities.detail(id),
    queryFn: async () => (await adminApi.getCharity()({ id })).data,
  });

  const dirtyPatch = form && initial ? diffToPatch(initial, form) : {};
  const isDirty = Object.keys(dirtyPatch).length > 0;

  useDirtyGuardedSeed(detail.data, isDirty, fromDetail, setForm, setInitial);

  function flash(tone: "success" | "danger", text: string) {
    setActionMessage({ tone, text });
    window.setTimeout(() => setActionMessage(null), 5000);
  }

  function refresh() {
    void qc.invalidateQueries({ queryKey: queryKeys.charities.detail(id) });
    void qc.invalidateQueries({ queryKey: queryKeys.charities.all });
  }

  const update = useMutation({
    mutationFn: async ({ patch }: { patch: UpdateCharityPatch; form: FormState }) =>
      (await adminApi.updateCharity()({ id, patch })).data,
    onSuccess: (_data, variables) => {
      flash("success", "Saved.");
      // Sync the pristine snapshot to the form that was actually submitted
      // (via mutation variables, not a closed-over `form`) so the form goes
      // pristine immediately and a subsequent refetch can re-seed normally.
      setInitial(variables.form);
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const setActive = useMutation({
    mutationFn: async (isActive: boolean) =>
      (await adminApi.setCharityActive()({ id, isActive })).data,
    onSuccess: ({ isActive }) => {
      flash("success", isActive ? "Reactivated." : "Deactivated.");
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const setVerified = useMutation({
    mutationFn: async ({ isVerified, reason }: { isVerified: boolean; reason?: string }) =>
      (await adminApi.setCharityVerified()({ id, isVerified, reason })).data,
    onSuccess: ({ isVerified }) => {
      flash("success", isVerified ? "Verified." : "Verification removed.");
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  function onSave() {
    if (!isDirty || !form) return;
    update.mutate({ patch: dirtyPatch, form });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/charities" })}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Charities
        </Button>
        <span className="text-muted-foreground">/</span>
        <span className="font-mono text-xs text-muted-foreground">{id}</span>
      </div>

      {detail.isLoading ? (
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">Loading…</CardContent>
        </Card>
      ) : detail.isError ? (
        <Card>
          <CardContent className="p-8 text-sm text-destructive">
            Failed to load charity: {(detail.error as Error).message}
          </CardContent>
        </Card>
      ) : detail.data && form && initial ? (
        <>
          <CharityHeader detail={detail.data} />

          {actionMessage ? (
            <div
              className={cn(
                "rounded-md border p-3 text-sm",
                actionMessage.tone === "success"
                  ? "border-success/40 bg-success/5 text-success"
                  : "border-destructive/40 bg-destructive/5 text-destructive",
              )}
            >
              {actionMessage.text}
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Identity
                </h2>
                <Field label="Name">
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </Field>
                <Field label="Description">
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={4}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder="Charity mission and purpose…"
                  />
                </Field>
                <Field label="Tax / Charity ID">
                  <Input
                    value={form.taxId}
                    onChange={(e) => setForm({ ...form, taxId: e.target.value })}
                    placeholder="e.g. 1063043"
                  />
                </Field>
                <Field label="Logo URL">
                  <Input
                    value={form.logoUrl}
                    onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                    placeholder="/charities/example/logo.png"
                  />
                </Field>
                <Field label="Banner URL">
                  <Input
                    value={form.bannerUrl}
                    onChange={(e) => setForm({ ...form, bannerUrl: e.target.value })}
                    placeholder="/charities/example/banner.jpg"
                  />
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Contact
                </h2>
                <Field label="Email">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </Field>
                <Field label="Website">
                  <Input
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="example.org.uk"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="grid gap-3 p-5 md:grid-cols-4">
                <h2 className="col-span-full text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Address
                </h2>
                <Field label="Street">
                  <Input
                    value={form.street}
                    onChange={(e) => setForm({ ...form, street: e.target.value })}
                  />
                </Field>
                <Field label="City">
                  <Input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                </Field>
                <Field label="State / Region">
                  <Input
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                  />
                </Field>
                <Field label="Postcode">
                  <Input
                    value={form.zipCode}
                    onChange={(e) =>
                      setForm({ ...form, zipCode: e.target.value.toUpperCase() })
                    }
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="grid gap-3 p-5 md:grid-cols-4">
                <h2 className="col-span-full text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Donation stats
                </h2>
                <StatCell label="Donations (agg)" value={detail.data.stats.totalDonations} />
                <StatCell
                  label="Total amount"
                  value={`£${detail.data.stats.totalAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                />
                <StatCell label="Items helped" value={detail.data.stats.itemsHelped} />
                <StatCell
                  label="Donation records"
                  value={detail.data.stats.donationsCount}
                  hint={
                    detail.data.stats.donationsCount !== detail.data.stats.totalDonations
                      ? "differs from aggregate"
                      : undefined
                  }
                />
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="space-y-3 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Actions
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={onSave} disabled={!isDirty || update.isPending}>
                    <Save className="mr-2 h-4 w-4" />
                    {update.isPending ? "Saving…" : isDirty ? "Save changes" : "No changes"}
                  </Button>
                  {detail.data.isVerified ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        const reason = window.prompt(
                          "Reason for removing verification (optional):",
                        );
                        if (reason === null) return;
                        setVerified.mutate({ isVerified: false, reason: reason || undefined });
                      }}
                      disabled={setVerified.isPending}
                    >
                      <ShieldOff className="mr-2 h-4 w-4" />
                      Remove verification
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => {
                        const reason = window.prompt(
                          "Reason for verifying (optional, audit-logged):",
                        );
                        if (reason === null) return;
                        setVerified.mutate({ isVerified: true, reason: reason || undefined });
                      }}
                      disabled={setVerified.isPending}
                    >
                      <BadgeCheck className="mr-2 h-4 w-4" />
                      Mark verified
                    </Button>
                  )}
                  {detail.data.isActive ? (
                    <Button
                      variant="destructive"
                      onClick={() => {
                        if (
                          window.confirm(
                            "Deactivate this charity? Users will no longer be able to donate to it.",
                          )
                        ) {
                          setActive.mutate(false);
                        }
                      }}
                      disabled={setActive.isPending}
                    >
                      <PowerOff className="mr-2 h-4 w-4" />
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setActive.mutate(true)}
                      disabled={setActive.isPending}
                    >
                      <Power className="mr-2 h-4 w-4" />
                      Reactivate
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      setForm(initial);
                      flash("success", "Reverted unsaved edits.");
                    }}
                    disabled={!isDirty || update.isPending}
                  >
                    Discard changes
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Edits, verification toggles, and active-state changes are audit-logged.
                  Hard delete is not exposed — deactivation hides the charity from the
                  donation picker while preserving historical donations.
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}

function CharityHeader({ detail }: { detail: AdminCharityDetail }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-start justify-between gap-4 p-5">
        <div className="space-y-1">
          <div className="text-xs font-mono text-muted-foreground">{detail.id}</div>
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            {detail.name}
            {detail.isVerified ? (
              <BadgeCheck className="h-5 w-5 text-primary" aria-label="Verified" />
            ) : null}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            {detail.isActive ? (
              <StatusPill tone="success">active</StatusPill>
            ) : (
              <StatusPill tone="neutral">inactive</StatusPill>
            )}
            {detail.isVerified ? (
              <StatusPill tone="info">verified</StatusPill>
            ) : (
              <StatusPill tone="warning">unverified</StatusPill>
            )}
            {detail.taxId ? (
              <span className="font-mono text-xs text-muted-foreground">
                Tax/Charity {detail.taxId}
              </span>
            ) : null}
            {detail.website ? (
              <a
                href={
                  detail.website.startsWith("http")
                    ? detail.website
                    : `https://${detail.website}`
                }
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                {detail.website.replace(/^https?:\/\//, "")}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-right text-xs">
          <span className="text-muted-foreground">Total raised</span>
          <span className="font-mono">
            £{detail.stats.totalAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
          <span className="text-muted-foreground">Donations</span>
          <span className="font-mono">{detail.stats.totalDonations.toLocaleString()}</span>
          <span className="text-muted-foreground">Created</span>
          <span className="font-mono">
            {detail.createdAt
              ? format(new Date(detail.createdAt), "yyyy-MM-dd")
              : "—"}
          </span>
          <span className="text-muted-foreground">Updated</span>
          <span className="font-mono">
            {detail.updatedAt
              ? format(new Date(detail.updatedAt), "yyyy-MM-dd")
              : "—"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCell({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div className="rounded-md border bg-card/50 p-3">
      <div className="text-[10px] font-medium uppercase text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      {hint ? <div className="text-[10px] text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
